import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  CertificateRequestAttachmentRef,
  CertificateRequestFormData,
  CertificateRequestItemRow,
  CertificateRequestTypeKey,
} from '../../types/applicationForm';
import {
  searchCffCustomers,
  type CffCustomerLookup,
} from '../../lib/customerFeedbackLookup';
import {
  searchCertificateItems,
  type CertificateItemLookup,
} from '../../lib/certificateRequestLookup';
import { fetchDemCustomerSalespersonCode } from '../../lib/demCustomers';
import type { SalesReportMatchResult } from '../../lib/certificateRequestSalesReportMatch';
import { CffAsyncTypeahead, type CffAsyncOption } from './CffAsyncTypeahead';

const TYPE_OPTIONS: { key: CertificateRequestTypeKey; label: string }[] = [
  { key: 'food_grade_sgs', label: 'Food Grade (SGS)' },
  { key: 'msds', label: 'MSDS' },
  { key: 'sfa', label: 'SFA' },
  { key: 'iso_22000', label: 'ISO 22000' },
  { key: 'others', label: 'Others' },
];

type CertificateRequestFormProps = {
  value: CertificateRequestFormData;
  onChange: (next: CertificateRequestFormData) => void;
  applicationNo?: string;
  disabled?: boolean;
  /** New request: pending customer-requirement files (not yet uploaded). */
  pendingCustomerRequirementFiles?: File[];
  onPendingCustomerRequirementFilesChange?: (files: File[]) => void;
  /** New request: pending optional 6-month sales report files. */
  pendingSalesReportFiles?: File[];
  onPendingSalesReportFilesChange?: (files: File[]) => void;
  /** Open a saved attachment (Authorised / Approver / requestor). */
  onOpenAttachment?: (ref: CertificateRequestAttachmentRef) => void;
  /** Best-effort match of form items vs sales report (new request only). */
  salesReportMatch?: SalesReportMatchResult | null;
};

function AttachmentList({
  label,
  hint,
  required,
  saved,
  pending,
  disabled,
  onPick,
  onOpen,
  onRemovePending,
}: {
  label: string;
  hint: string;
  required?: boolean;
  saved: CertificateRequestAttachmentRef[];
  pending: File[];
  disabled: boolean;
  onPick?: (files: FileList | null) => void;
  onOpen?: (ref: CertificateRequestAttachmentRef) => void;
  onRemovePending?: (index: number) => void;
}) {
  return (
    <div className="puf-form__attachments ctr-form__attachments">
      <div className="field-label">
        {label}
        {required ? <span className="af-form__req"> *</span> : null}
      </div>
      <p className="field-hint">{hint}</p>
      {!disabled && onPick ? (
        <label className="btn-secondary btn-sm puf-form__upload-btn">
          Add files
          <input
            type="file"
            multiple
            hidden
            onChange={(e) => {
              onPick(e.target.files);
              e.target.value = '';
            }}
          />
        </label>
      ) : null}
      <ul className="puf-form__file-list">
        {saved.map((ref) => (
          <li key={ref.path}>
            {onOpen ? (
              <button type="button" className="puf-form__file-link" onClick={() => onOpen(ref)}>
                {ref.fileName}
              </button>
            ) : (
              <span>{ref.fileName}</span>
            )}
          </li>
        ))}
        {pending.map((file, index) => (
          <li key={`pending-${file.name}-${index}`}>
            <span>{file.name} (pending upload)</span>
            {!disabled && onRemovePending ? (
              <button
                type="button"
                className="btn-secondary btn-sm"
                onClick={() => onRemovePending(index)}
              >
                Remove
              </button>
            ) : null}
          </li>
        ))}
        {saved.length === 0 && pending.length === 0 ? (
          <li className="puf-form__file-empty">No attachments</li>
        ) : null}
      </ul>
    </div>
  );
}

function setItem(
  items: CertificateRequestItemRow[],
  index: number,
  patch: Partial<CertificateRequestItemRow>,
): CertificateRequestItemRow[] {
  return items.map((row, i) => (i === index ? { ...row, ...patch } : row));
}

function customerFromMeta(meta: unknown): CffCustomerLookup | null {
  if (!meta || typeof meta !== 'object') return null;
  return meta as CffCustomerLookup;
}

function itemFromMeta(meta: unknown): CertificateItemLookup | null {
  if (!meta || typeof meta !== 'object') return null;
  return meta as CertificateItemLookup;
}

function inputClass(readOnly: boolean): string {
  return readOnly ? 'field-input field-input--readonly' : 'field-input';
}

export function CertificateRequestForm({
  value,
  onChange,
  applicationNo,
  disabled = false,
  pendingCustomerRequirementFiles = [],
  onPendingCustomerRequirementFilesChange,
  pendingSalesReportFiles = [],
  onPendingSalesReportFilesChange,
  onOpenAttachment,
  salesReportMatch = null,
}: CertificateRequestFormProps) {
  const [customerLocked, setCustomerLocked] = useState(() => Boolean(value.customer_no.trim()));
  const [salesLoading, setSalesLoading] = useState(false);
  const [generalOpen, setGeneralOpen] = useState(true);
  const [itemsOpen, setItemsOpen] = useState(true);
  const [attachmentsOpen, setAttachmentsOpen] = useState(true);
  const lockedForRef = useRef<string | undefined>(undefined);
  const valueRef = useRef(value);
  valueRef.current = value;
  const customerReadOnly = customerLocked || disabled;
  const canEditAttachments = Boolean(
    !disabled &&
      (onPendingCustomerRequirementFilesChange || onPendingSalesReportFilesChange),
  );

  useEffect(() => {
    const key = applicationNo ?? '__new__';
    if (lockedForRef.current === key) return;
    lockedForRef.current = key;
    setCustomerLocked(Boolean(value.customer_no.trim()));
  }, [applicationNo, value.customer_no]);

  const patch = (partial: Partial<CertificateRequestFormData>) =>
    onChange({ ...valueRef.current, ...partial });

  const searchCustomers = useCallback(async (query: string): Promise<CffAsyncOption[]> => {
    const rows = await searchCffCustomers(query);
    return rows.map((row) => ({
      value: row.no,
      label: row.name ? `${row.no} — ${row.name}` : row.no,
      meta: row,
    }));
  }, []);

  const searchItems = useCallback(async (query: string): Promise<CffAsyncOption[]> => {
    const rows = await searchCertificateItems(query);
    return rows.map((row) => ({
      value: row.code,
      label: row.description ? `${row.code} — ${row.description}` : row.code,
      meta: row,
    }));
  }, []);

  const applyCustomer = async (customer: CffCustomerLookup) => {
    // Snapshot from lookup only when the user explicitly picks a customer — never on load.
    if (disabled) return;
    onChange({
      ...valueRef.current,
      customer_no: customer.no,
      customer_name: customer.name,
      sales_code: '',
      contact_person: customer.contact || valueRef.current.contact_person,
      contact_no: customer.phone_no || valueRef.current.contact_no,
    });
    setCustomerLocked(true);
    setSalesLoading(true);
    try {
      const salesCode = await fetchDemCustomerSalespersonCode(customer.no);
      if (valueRef.current.customer_no.trim().toUpperCase() !== customer.no.trim().toUpperCase()) {
        return;
      }
      onChange({
        ...valueRef.current,
        sales_code: salesCode,
      });
    } finally {
      setSalesLoading(false);
    }
  };

  const clearCustomer = () => {
    if (disabled) return;
    onChange({
      ...valueRef.current,
      customer_no: '',
      customer_name: '',
      sales_code: '',
      contact_person: '',
      contact_no: '',
      email: '',
    });
    setCustomerLocked(false);
  };

  const toggleType = (key: CertificateRequestTypeKey) => {
    if (disabled) return;
    const next = !valueRef.current.types[key];
    patch({
      types: { ...valueRef.current.types, [key]: next },
      type_others: key === 'others' && !next ? '' : valueRef.current.type_others,
    });
  };

  const showItemSection =
    value.types.food_grade_sgs || value.types.msds || value.types.others;

  return (
    <div className={`ctr-form${disabled ? ' is-readonly' : ''}`}>
      <section className={`ctr-form__panel${generalOpen ? '' : ' is-collapsed'}`}>
        <div className="ctr-form__panel-head">
          <button
            type="button"
            className="ctr-form__panel-toggle"
            aria-expanded={generalOpen}
            onClick={() => setGeneralOpen((open) => !open)}
          >
            <span className="ctr-form__chevron" aria-hidden>
              {generalOpen ? '▾' : '▸'}
            </span>
            <span className="ctr-form__panel-title">General</span>
          </button>
        </div>

        {generalOpen ? (
          <div className="ctr-form__panel-body">
            <div className="ctr-form__customer-code">
              <label className="field">
                <span className="field-label">Customer No.</span>
                {customerReadOnly ? (
                  <input className={inputClass(true)} value={value.customer_no} readOnly />
                ) : (
                  <CffAsyncTypeahead
                    value={value.customer_no}
                    onChange={(next) => patch({ customer_no: next })}
                    onSelect={(opt) => {
                      const customer = customerFromMeta(opt.meta);
                      if (customer) void applyCustomer(customer);
                      else patch({ customer_no: opt.value });
                    }}
                    search={searchCustomers}
                    placeholder="Search customer no. or name"
                    ariaLabel="Customer number"
                  />
                )}
              </label>
              {!disabled ? (
                <div className="ctr-form__customer-code-actions">
                  {customerLocked ? (
                    <button type="button" className="btn-secondary btn-sm" onClick={clearCustomer}>
                      Change
                    </button>
                  ) : null}
                </div>
              ) : null}
            </div>

            <div className="ctr-form__grid2">
              <label className="field">
                <span className="field-label">Customer Name</span>
                <input
                  className={inputClass(true)}
                  value={value.customer_name}
                  readOnly
                  placeholder={customerLocked ? '' : 'Select customer first'}
                />
              </label>
              <label className="field">
                <span className="field-label">Sales Code</span>
                <input
                  className={inputClass(true)}
                  value={salesLoading ? 'Loading…' : value.sales_code}
                  readOnly
                  placeholder={customerLocked ? 'From customer list' : 'Select customer first'}
                />
              </label>
            </div>

            <div className="ctr-form__grid2">
              <label className="field">
                <span className="field-label">Contact Person</span>
                <input
                  className={inputClass(disabled)}
                  value={value.contact_person}
                  disabled={disabled}
                  onChange={(e) => patch({ contact_person: e.target.value })}
                />
              </label>
              <label className="field">
                <span className="field-label">Contact No.</span>
                <input
                  className={inputClass(disabled)}
                  value={value.contact_no}
                  disabled={disabled}
                  onChange={(e) => patch({ contact_no: e.target.value })}
                />
              </label>
            </div>

            <label className="field">
              <span className="field-label">Email</span>
              <input
                type="email"
                className={inputClass(disabled)}
                value={value.email}
                disabled={disabled}
                onChange={(e) => patch({ email: e.target.value })}
              />
            </label>

            <div className="ctr-form__type-block">
              <span className="field-label">Type</span>
              <fieldset className="ctr-form__type-grid" disabled={disabled}>
                {TYPE_OPTIONS.map((opt) => {
                  const checked = value.types[opt.key];
                  const chip = (
                    <label
                      className={`ctr-form__type-chip${checked ? ' is-selected' : ''}${
                        disabled ? ' is-disabled' : ''
                      }`}
                    >
                      <input
                        type="checkbox"
                        className="ctr-form__type-chip-input"
                        checked={checked}
                        onChange={() => toggleType(opt.key)}
                      />
                      <span className="ctr-form__type-chip-mark" aria-hidden>
                        {checked ? '✓' : ''}
                      </span>
                      <span className="ctr-form__type-chip-label">{opt.label}</span>
                    </label>
                  );

                  if (opt.key !== 'others') {
                    return (
                      <span key={opt.key} className="ctr-form__type-chip-wrap">
                        {chip}
                      </span>
                    );
                  }

                  return (
                    <div key={opt.key} className="ctr-form__type-others-group">
                      {chip}
                      {checked ? (
                        <input
                          className={`field-input ctr-form__type-others-input${
                            disabled ? ' field-input--readonly' : ''
                          }`}
                          value={value.type_others}
                          disabled={disabled}
                          placeholder="Specify other certificate type"
                          aria-label="Other certificate type"
                          onChange={(e) => patch({ type_others: e.target.value })}
                        />
                      ) : null}
                    </div>
                  );
                })}
              </fieldset>
            </div>
          </div>
        ) : null}
      </section>

      {showItemSection ? (
        <section className={`ctr-form__panel${itemsOpen ? '' : ' is-collapsed'}`}>
          <div className="ctr-form__panel-head">
            <button
              type="button"
              className="ctr-form__panel-toggle"
              aria-expanded={itemsOpen}
              onClick={() => setItemsOpen((open) => !open)}
            >
              <span className="ctr-form__chevron" aria-hidden>
                {itemsOpen ? '▾' : '▸'}
              </span>
              <span className="ctr-form__panel-title">Item</span>
            </button>
          </div>

        {itemsOpen ? (
          <div className="ctr-form__panel-body">
            <div className="ctr-form__table-wrap">
              <table className="ctr-form__table">
                <thead>
                  <tr>
                    <th className="ctr-form__col-no">No.</th>
                    <th>Item No.</th>
                    <th>Vendor No.</th>
                    <th>Material</th>
                    <th>Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  {value.items.map((row, index) => (
                    <tr key={`ctr-item-${index}`}>
                      <td className="ctr-form__col-no">{index + 1}</td>
                      <td>
                        {disabled ? (
                          <input className={inputClass(true)} value={row.item_no} readOnly />
                        ) : (
                          <CffAsyncTypeahead
                            value={row.item_no}
                            onChange={(next) =>
                              patch({
                                items: setItem(value.items, index, {
                                  item_no: next,
                                  vendor_no: '',
                                  material: '',
                                }),
                              })
                            }
                            onSelect={(opt) => {
                              const item = itemFromMeta(opt.meta);
                              patch({
                                items: setItem(value.items, index, {
                                  item_no: opt.value,
                                  vendor_no: item?.vendor_no?.trim() || '',
                                  material: item?.material?.trim() || '',
                                }),
                              });
                            }}
                            search={searchItems}
                            placeholder="Item no."
                            ariaLabel={`Item ${index + 1} number`}
                          />
                        )}
                      </td>
                      <td>
                        <input
                          className={inputClass(true)}
                          value={row.vendor_no}
                          readOnly
                          placeholder="From item list"
                        />
                      </td>
                      <td>
                        <input
                          className={inputClass(true)}
                          value={row.material}
                          readOnly
                          placeholder="From item list"
                        />
                      </td>
                      <td>
                        <input
                          className={inputClass(disabled)}
                          value={row.remarks}
                          disabled={disabled}
                          onChange={(e) =>
                            patch({
                              items: setItem(value.items, index, { remarks: e.target.value }),
                            })
                          }
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}
        </section>
      ) : null}

      <section className={`ctr-form__panel${attachmentsOpen ? '' : ' is-collapsed'}`}>
        <div className="ctr-form__panel-head">
          <button
            type="button"
            className="ctr-form__panel-toggle"
            aria-expanded={attachmentsOpen}
            onClick={() => setAttachmentsOpen((open) => !open)}
          >
            <span className="ctr-form__chevron" aria-hidden>
              {attachmentsOpen ? '▾' : '▸'}
            </span>
            <span className="ctr-form__panel-title">Attachments</span>
          </button>
        </div>

        {attachmentsOpen ? (
          <div className="ctr-form__panel-body">
            <AttachmentList
              label="Customer requirement"
              hint="Optional for now. Screenshots, emails, or any files from the customer. Any format · multiple files · no size limit."
              saved={value.customer_requirement_attachments}
              pending={pendingCustomerRequirementFiles}
              disabled={!canEditAttachments || !onPendingCustomerRequirementFilesChange}
              onPick={
                onPendingCustomerRequirementFilesChange
                  ? (list) => {
                      if (!list?.length) return;
                      onPendingCustomerRequirementFilesChange([
                        ...pendingCustomerRequirementFiles,
                        ...Array.from(list),
                      ]);
                    }
                  : undefined
              }
              onOpen={onOpenAttachment}
              onRemovePending={
                onPendingCustomerRequirementFilesChange
                  ? (index) => {
                      onPendingCustomerRequirementFilesChange(
                        pendingCustomerRequirementFiles.filter((_, i) => i !== index),
                      );
                    }
                  : undefined
              }
            />

            <AttachmentList
              label="6-month item sales report"
              hint="Optional. Usually a PDF of the last 6 months’ item sales. Upload when the customer has purchase history so we can match item nos. Skip for new customers / no sales record. PDF, Excel, or CSV accepted."
              saved={value.sales_report_attachments}
              pending={pendingSalesReportFiles}
              disabled={!canEditAttachments || !onPendingSalesReportFilesChange}
              onPick={
                onPendingSalesReportFilesChange
                  ? (list) => {
                      if (!list?.length) return;
                      onPendingSalesReportFilesChange([
                        ...pendingSalesReportFiles,
                        ...Array.from(list),
                      ]);
                    }
                  : undefined
              }
              onOpen={onOpenAttachment}
              onRemovePending={
                onPendingSalesReportFilesChange
                  ? (index) => {
                      onPendingSalesReportFilesChange(
                        pendingSalesReportFiles.filter((_, i) => i !== index),
                      );
                    }
                  : undefined
              }
            />

            {salesReportMatch &&
            (salesReportMatch.matched.length > 0 ||
              salesReportMatch.unmatched.length > 0 ||
              salesReportMatch.note) ? (
              <div className="ctr-form__sales-match" role="status">
                <div className="field-label">Sales report item match</div>
                {salesReportMatch.matched.length > 0 ? (
                  <p className="field-hint">
                    Found on report: {salesReportMatch.matched.join(', ')}
                  </p>
                ) : null}
                {salesReportMatch.unmatched.length > 0 ? (
                  <p className="field-hint">
                    Not found on report (you can still proceed):{' '}
                    {salesReportMatch.unmatched.join(', ')}
                  </p>
                ) : null}
                {salesReportMatch.note ? (
                  <p className="field-hint">{salesReportMatch.note}</p>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}
      </section>
    </div>
  );
}
